// BatiAssistPipelinesPage.jsx
// Page Pipelines Commerciaux dans le contexte Espace Pro Abracadabati

import { motion } from 'framer-motion';
import { 
  LayoutDashboard, ArrowRight, Target, TrendingUp, 
  Filter, Bell, BarChart3, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const features = [
  { icon: Target, title: "Vue Kanban", desc: "Visualise tes affaires par étape : prospect, devis envoyé, négociation, gagné" },
  { icon: TrendingUp, title: "Prévision CA", desc: "Anticipe ton chiffre d'affaires grâce aux probabilités de conclusion" },
  { icon: Filter, title: "Filtres avancés", desc: "Filtre par montant, date, commercial, type de projet..." },
  { icon: Bell, title: "Relances auto", desc: "Reçois des alertes pour ne jamais oublier de relancer un prospect" },
  { icon: BarChart3, title: "Statistiques", desc: "Analyse tes taux de conversion et identifie les points d'amélioration" },
  { icon: Users, title: "Multi-utilisateurs", desc: "Attribue des affaires à tes commerciaux et suis leur performance" },
];

const stages = [
  { name: "Prospect", color: "bg-blue-500", count: 12 },
  { name: "Devis envoyé", color: "bg-yellow-500", count: 8 },
  { name: "Négociation", color: "bg-orange-500", count: 5 },
  { name: "Gagné", color: "bg-green-500", count: 15 },
];

export default function BatiAssistPipelinesPage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="min-h-screen bg-white" data-testid="batiassist-pipelines-page">
        <UnifiedHeader />
        <EspaceProNav />
      
      <main>
        {/* Hero */}
        <section className="pt-16 pb-16 md:pt-20 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
                <LayoutDashboard className="w-4 h-4" />
                Pipelines Commerciaux
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
                Suis tes affaires de A à Z{' '}
                <span className="text-[#F97316]">en un coup d'œil</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-10">
                Visualise toutes tes opportunités commerciales sur un tableau Kanban intuitif. 
                Ne laisse plus jamais une affaire passer entre les mailles du filet.
              </p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                  Découvrir les pipelines
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>

        {/* Pipeline Preview */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Un pipeline <span className="text-[#F97316]">visuel et intuitif</span>
              </h2>
            </motion.div>

            {/* Mini Pipeline Demo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {stages.map((stage, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-4 bg-slate-50 rounded-xl"
                >
                  <div className={`w-full h-2 ${stage.color} rounded-full mb-3`} />
                  <h3 className="font-bold text-slate-800">{stage.name}</h3>
                  <p className="text-2xl font-bold text-[#1E3A5F]">{stage.count}</p>
                  <p className="text-sm text-slate-500">affaires</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-6 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow"
                  >
                    <div className="w-14 h-14 rounded-xl bg-[#1E3A5F] flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F]">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Prêt à booster ton suivi commercial ?
              </h2>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold rounded-full px-10 py-6 text-lg">
                  Réserver mon diagnostic gratuit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </div>
        </section>
      </main>
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
